import React, { PropsWithChildren, ReactNode } from 'react';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { DivProps, SpanProps } from '../../../types/HtmlProps';
import { CollapsibleProps } from '../../../hooks/types/useCollapsibleOverlay';
import { appendClassNames } from '../../../lib/bemUtils/classname';
import bem from '../../../lib/bemUtils/bem';

export enum CollapsibleOverlayModifier {
  SELECT_A_LOT = 'select-a-lot',
}

type CollapsibleContentProps = DivProps &
  CollapsibleProps<HTMLDivElement> & {
    ref?: React.RefObject<HTMLDivElement | null>;
    modifier?: Modifier<CollapsibleOverlayModifier>;
  };

/**
 * Sometimes it is helpful to have children in DOM, especially on form elements with data sent to the backend.
 * See MultiSelect.
 */
const returnChildrenOrNothing = (isOpen: boolean, returnChildrenWhenClosed: boolean, children: ReactNode) => {
  if (isOpen || returnChildrenWhenClosed) {
    return (
      <div className="collapsible-overlay__outer-wrapper">
        <div className="collapsible-overlay__arrow-container"></div>
        <div className="collapsible-overlay__menu-container">{children}</div>
        <div className="collapsible-overlay__arrow-container"></div>
      </div>
    );
  }

  return null;
};

/**
 * Base CollapsibleContent component capable of being rendered as `htmlTag`
 */
export const CollapsibleContent = ({
  children,
  returnChildrenWhenClosed = false,
  isOpen,
  className,
  modifier,
  ref,
  ...props
}: PropsWithChildren<CollapsibleContentProps>) => {
  return (
    <div {...props} className={appendClassNames(className, bem('collapsible-overlay', modifier))} ref={ref}>
      {returnChildrenOrNothing(isOpen, returnChildrenWhenClosed, children)}
    </div>
  );
};
type CollapsibleContentSpanProps = SpanProps &
  CollapsibleProps<HTMLSpanElement> & {
    ref?: React.Ref<HTMLSpanElement>;
    modifier?: Modifier<CollapsibleOverlayModifier>;
  };
/**
 * Base CollapsibleContent component capable of being rendered as `htmlTag`
 */
export const CollapsibleContentSpan = ({
  children,
  isOpen,
  returnChildrenWhenClosed = false,
  className,
  modifier,
  ref,
  ...props
}: PropsWithChildren<CollapsibleContentSpanProps>) => {
  return (
    <span {...props} className={appendClassNames(className, bem('collapsible-overlay', modifier))} ref={ref}>
      {returnChildrenOrNothing(isOpen, returnChildrenWhenClosed, children)}
    </span>
  );
};
